import { gql } from '@apollo/client';

/* ── interactive stock research sessions (the analyst agent) ───────────────
   A session is one stock researched in conversation: StartStockResearch opens
   it and writes revision 1 of the initiation report, the chat stream
   (subscriptions/stockAnalyst.js) edits it into later revisions, and the
   analyst's proposed actions wait here until confirmed or rejected. Every read
   below needs an employee login. */

/** One session's state — currentRevisionId is the latest report revision. */
export const STOCK_ANALYST_SESSION = gql`
  query StockAnalystSession($sessionId: Int!) {
    stockAnalystSession(sessionId: $sessionId) {
      id
      analystKind
      securitySymbol
      title
      status
      currentRevisionId
      finalRevisionId
      companyReportId
      alphaThesisId
      tradeThesisId
      livekitThreadName
      assumptionOverrides
      createdAt
      updatedAt
    }
  }
`;

/** The reader's own sessions, newest first; narrowed to one stock when given. */
export const STOCK_ANALYST_SESSIONS = gql`
  query StockAnalystSessions($securitySymbol: String, $analystKind: String, $limit: Int) {
    stockAnalystSessions(securitySymbol: $securitySymbol, analystKind: $analystKind, limit: $limit) {
      id
      securitySymbol
      title
      status
      currentRevisionId
      finalRevisionId
      createdAt
    }
  }
`;

/** Every revision of the session's report, oldest first — the edit history. */
export const STOCK_ANALYST_REVISIONS = gql`
  query StockAnalystRevisions($sessionId: Int!) {
    stockAnalystRevisions(sessionId: $sessionId) {
      id
      revisionNumber
      author
      changeSummary
      headline
      isFinal
      createdAt
    }
  }
`;

/**
 * One revision in full. `format` "markdown" is read by lib/reportMarkdown and
 * laid out by CompanyReport; "html" is the print edition with the exhibits
 * embedded as images.
 */
export const STOCK_ANALYST_REVISION = gql`
  query StockAnalystRevision($revisionId: Int!, $format: String!) {
    stockAnalystRevision(revisionId: $revisionId, format: $format) {
      format
      body
      revision {
        id
        sessionId
        securitySymbol
        revisionNumber
        parentRevisionId
        author
        changeSummary
        diff
        headline
        knowledgeVersion
        isFinal
        lockedAt
        pdfS3Key
        companyReportId
        alphaThesisId
        tradeThesisId
        createdAt
      }
    }
  }
`;

/** The actions the analyst proposed in a session: proposed | confirmed | rejected | expired. */
export const STOCK_ANALYST_PENDING_ACTIONS = gql`
  query StockAnalystPendingActions($sessionId: Int!) {
    stockAnalystPendingActions(sessionId: $sessionId) {
      id
      revisionId
      action
      payload
      preview
      status
      proposedBy
      proposedAt
      expiresAt
      decidedAt
      decidedBy
      result
    }
  }
`;
